import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DueRule } from '@miraki/contracts';
import {
  useAccountSetupDefaults, useApplicationDocuments, useApplications, useCountries, useDecideApplication,
} from '../api/hooks';
import { applicationApprovalWarning } from './kyc-view';
import { ApiError } from '../api/client';
import type { Application } from '../api/types';
import { useSession } from '../auth/session';
/* D75 — the ONE due-rule formatter, shared with A-04, R-18, R-22 and A-40. */
import { dueRuleWords } from './payables-view';
import { Alert, Button, Chip, Empty, Field, PageHeader, Panel, Rail, Spinner, type Tone } from '../ui';
import { KycEvidence } from './KycEvidence';

const TONE: Record<Application['status'], Tone> = {
  PENDING: 'us', INFO_REQUESTED: 'them', APPROVED: 'done', REJECTED: 'stop',
};

/**
 * D75 — the extra field `/applications/account-setup-defaults` now sends: the portal's due rule,
 * resolved, so this panel can say what leaving the payment-terms box blank will actually do.
 *
 * Declared here rather than on `AccountSetupDefaults` in `api/hooks.ts` only because that file
 * is settling under other work and must not be touched. It belongs beside the other three fields
 * and should move there once that work lands.
 */
type SetupDefaults = { payableDueRule: DueRule };

/** A-02 — archetype 7: work queue on the left, the decision on the right. */
export function Applications() {
  /*
   * The acting portal's capabilities, read from the same place the server reads them. A
   * market that charges no subscription is not asked for one — D46, so this is a capability
   * and never a check on the portal's code.
   */
  const { t } = useTranslation();
  const { can, offers } = useSession();
  const offersSubscriptions = offers('subscriptions');
  const { data, isLoading, error } = useApplications();
  /*
   * ON-05 — what this portal gives an account approved with the setup boxes blank. The panel
   * needs it to say what blank MEANS: an empty tolerance box reads as "no tolerance" when it in
   * fact means "the portal's figure", and those are opposite answers. Read from the portal's
   * settings, never from which portal it is (D46).
   */
  const { data: setupDefaults } = useAccountSetupDefaults();
  /*
   * D75 — the portal's rule as a PHRASE, for the hint that names it inside a sentence. The
   * fallback is the platform default the API also falls back to, so the hint is never blank
   * while the request is in flight.
   */
  const portalRulePhrase = dueRuleWords(
    (setupDefaults as (typeof setupDefaults & SetupDefaults) | undefined)?.payableDueRule
      ?? { type: 'NET_DAYS', days: 30, source: 'default' },
    'phrase',
  );
  const { data: countries } = useCountries();
  const decide = useDecideApplication();
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [failed, setFailed] = useState<string | null>(null);
  /*
   * What just happened, said out loud.
   *
   * An approved application leaves this queue immediately and the screen jumps to the next
   * one waiting, so the only evidence the decision landed was the row disappearing — which
   * looks identical to a misclick on a different row. Only a FAILED credential email said
   * anything, which meant the quiet outcome was the successful one.
   */
  const [outcome, setOutcome] = useState<{ tone: 'done' | 'us'; text: string } | null>(null);
  const [mailWarning, setMailWarning] = useState<string | null>(null);
  /*
   * ON-05's account setup. Blank means "use the portal's default budget", and the
   * API refuses the approval when the portal has none — the portal decides what is
   * required, not this screen and not a country (D46, SC-07). The server's message
   * names the portal and lands in `failed` below.
   */
  const [budget, setBudget] = useState('');
  /*
   * BG-04 — the account's tolerance band (client, 29-Oct-2026). Blank means "take the portal's
   * figure", which `setupDefaults` below puts in the placeholder so the empty box says what it
   * means. '0' is the other answer: this account gets nothing over its budget.
   */
  const [tolerance, setTolerance] = useState('');
  const [budgetReason, setBudgetReason] = useState('');
  const [termsDays, setTermsDays] = useState('30');
  /*
   * SB-01 — the first subscription term, where the market charges one. The server still
   * decides; this screen no longer ASKS where the answer can only be "no". The refusal names the
   * portal. Sending it to a market that charges none is refused rather than ignored, so these
   * stay blank unless the admin is told to fill them.
   */
  const [subStart, setSubStart] = useState('');
  const [subEnd, setSubEnd] = useState('');
  const [subFee, setSubFee] = useState('');
  const [subRef, setSubRef] = useState('');

  /*
   * Derived BEFORE the early returns below, because the hook at the end of this block needs the
   * current application's id and a hook may not be called after a return. Both only need `data ?? []`.
   */
  const open = (data ?? []).filter((a) => a.status === 'PENDING' || a.status === 'INFO_REQUESTED');
  // ON-05/ON-07 — a decision is final (`mustFindOpen` refuses to re-decide an
  // APPROVED or REJECTED row), so these are read-only history, not a second
  // queue: the row stays in the database exactly as decided, just no longer
  // waiting on anyone.
  const decided = (data ?? []).filter((a) => a.status === 'APPROVED' || a.status === 'REJECTED');
  const current = open.find((a) => a.id === selected) ?? open[0] ?? null;
  // D105 — the same rows the evidence panel shows (one query, shared), so the warning and the table
  // beside it cannot disagree about whether there is a pack. `undefined` while loading: no warning
  // is shown for a pack that has merely not arrived yet.
  const packRows = useApplicationDocuments(current?.id);
  const approvalWarning = packRows.data ? applicationApprovalWarning(packRows.data.length) : null;


  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  async function act(kind: 'approve' | 'reject' | 'request-information') {
    if (!current) return;
    setFailed(null);
    setMailWarning(null);
    setOutcome(null);
    const contactEmail = current.contact.email;
    if (kind === 'approve') {
      // The same rules the API applies, said before the request so the reason is next to the box.
      const b = budget.trim();
      if (b && !/^\d+(\.\d{1,4})?$/.test(b)) {
        setFailed('The inventory budget must be a plain number such as 40000.00 — no commas, spaces or currency symbols.');
        return;
      }
      if (b && !(Number(b) > 0)) { setFailed('The inventory budget must be greater than zero.'); return; }
      /*
       * BG-04 — the same rule `zPercent` applies on the wire, said here so the reason sits next
       * to the box. Blank is NOT an error: it means this account takes the portal's figure.
       */
      const tol = tolerance.trim();
      if (tol && !/^\d{1,3}(\.\d{1,4})?$/.test(tol)) {
        setFailed('The tolerance must be a percentage such as 5 or 7.5, with at most four decimal places.');
        return;
      }
      if (tol && Number(tol) > 100) { setFailed('The tolerance cannot be more than 100%.'); return; }
      if (budgetReason.trim() && budgetReason.trim().length < 3) {
        setFailed('"Why this budget" needs at least 3 characters, or leave it blank.');
        return;
      }
      const days = Number(termsDays);
      if (termsDays.trim() !== '' && (!Number.isInteger(days) || days < 0 || days > 180)) {
        setFailed('Payment terms (net days) must be a whole number from 0 to 180.');
        return;
      }
    }
    try {
      const result = await decide.mutateAsync({
        id: current.id,
        decision: kind,
        /*
         * ON-05 — what Admin actually decided, and nothing it did not.
         *
         * This used to post `tier: 'New', advancePct: '0', creditLimit: '0',
         * incoterm: 'DAP'` on every approval: four values nobody chose, three of
         * them from a commercial model v1.6 retired. An omitted budget now means
         * "inherit the portal default", which the API accepts or refuses per portal.
         */
        body: kind === 'approve'
          ? {
            ...(budget.trim() ? { inventoryBudget: budget.trim() } : {}),
            /*
             * BG-04 — sent only when Admin typed one. Omitted leaves the column null, which
             * means "take the portal's figure" and is NOT the same as zero. Sending a resolved
             * 5 here would freeze today's portal setting onto the account for ever.
             */
            ...(tolerance.trim() ? { budgetTolerancePct: tolerance.trim() } : {}),
            ...(budgetReason.trim() ? { budgetReason: budgetReason.trim() } : {}),
            /*
             * D75 — sent only when Admin typed a term, exactly as `budgetTolerancePct` above.
             *
             * `Number(termsDays) || 30` was wrong in two directions at once. Blank became 30, so
             * every approval asserted a net-30 term nobody chose — which now takes the account
             * off the portal's rule. And a typed **0** is falsy, so "payable immediately" was
             * silently rewritten to net 30, which is the one answer it is not.
             */
            ...(termsDays.trim() ? { paymentTermsDays: Number(termsDays) } : {}),
            /*
             * `offersSubscriptions` guards the SEND as well as the fields. Hiding inputs is a
             * display decision; this is the one that matters. Switch portal with a term half
             * typed and the state survives the switch — without this, GCC would post a
             * subscription it does not charge and the approval would be refused for a field
             * the person can no longer even see.
             */
            ...(offersSubscriptions && subStart.trim() && subEnd.trim() && subFee.trim() && subRef.trim()
              ? {
                subscription: {
                  termStart: subStart.trim(),
                  termEnd: subEnd.trim(),
                  fee: subFee.trim(),
                  feeReference: subRef.trim(),
                },
              }
              : {}),

          }
          : kind === 'reject' ? { reason } : { note: reason },
      });
      // ON-05 — there is no password-reset flow, so a credential that never
      // sent is unrecoverable. The retailer was created regardless; the admin
      // needs to know the login mail didn't go out, not just that it approved.
      // The application drops off this queue the moment it's approved, so
      // this can't be shown against `current` — it's its own persistent banner.
      const name = current.businessName;
      if (kind === 'approve') {
        const sent = (result as { credentialEmailSent?: boolean } | undefined)?.credentialEmailSent;
        if (sent === false) {
          /*
           * The comment that used to sit here said a credential which never sent is
           * unrecoverable, "there is no password-reset flow". There is now: A-06 can set a
           * password on any trade login. So this stops being a dead end and becomes an
           * instruction — the account exists and is reachable, somebody just has to issue
           * the password by hand.
           */
          setMailWarning(
            `${name} was approved, but the account-ready email to ${contactEmail} did not send. `
            + 'The login exists — set a password for it on the Users screen and pass it on.',
          );
        } else {
          // D105 — approving with no KYC documents is allowed, and said rather than silent: the
          // server's own warning rides back in the result.
          const documentWarning = (result as { documentWarning?: string | null } | undefined)?.documentWarning;
          setOutcome({
            tone: 'done',
            text: `${name} approved. ${contactEmail} is now its Owner login, and a temporary `
              + 'password has been emailed — they must change it at first sign-in.'
              + (documentWarning ? ` ${documentWarning}` : ''),
          });
        }
      } else if (kind === 'reject') {
        setOutcome({ tone: 'us', text: `${name} rejected. The record and your reason are kept.` });
      } else {
        setOutcome({ tone: 'us', text: `More information requested from ${name}.` });
      }
      setSelected(null);
      setReason('');
      setBudget('');
      setTolerance('');
      setBudgetReason('');
      setTermsDays('30');
      setSubStart(''); setSubEnd(''); setSubFee(''); setSubRef('');
    } catch (e) {
      setFailed(e instanceof ApiError ? e.message : 'Could not record the decision');
    }
  }

  const pack = countries?.find((c) => c.code === current?.countryCode);

  return (
    <>
      <PageHeader
        title="Retailer applications"
      />
      {outcome ? <Alert tone={outcome.tone}>{outcome.text}</Alert> : null}
      {mailWarning ? <Alert tone="us">{mailWarning}</Alert> : null}
      {open.length === 0 ? (
        <Panel><Empty title="Queue clear" body="No applications waiting on this portal." /></Panel>
      ) : (
        <div className="mk-queue2">
          <Panel title={`${open.length} waiting`}>
            {open.map((a) => (
              <button
                key={a.id}
                onClick={() => { setSelected(a.id); setFailed(null); }}
                className="mk-qrow"
                aria-selected={a.id === current?.id}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '11px 14px', border: 0,
                  borderBottom: '1px solid var(--mk-rule)',
                  borderLeft: `2px solid ${a.id === current?.id ? 'var(--mk-brand)' : 'transparent'}`,
                  background: a.id === current?.id ? 'var(--mk-brand-wash)' : 'var(--mk-surface)',
                  font: 'inherit', color: 'inherit',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Chip tone={TONE[a.status]}>{a.status.replace('_', ' ')}</Chip>
                  <b>{a.businessName}</b>
                  <span className="mk-spacer" />
                  <span className="mk-hint">{a.ageDays}d</span>
                </div>
                <div className="mk-hint" style={{ marginTop: 3 }}>
                  {a.reference} · {a.city ?? '—'}, {a.countryCode} · {a.contact.email}
                </div>
              </button>
            ))}
          </Panel>

          {current ? (
            <Panel
              title={current.reference}
              actions={<Chip tone={TONE[current.status]}>{current.status.replace('_', ' ')}</Chip>}
            >
              <div className="mk-pbody">
                <h2 style={{ fontFamily: 'var(--mk-font-display)', fontWeight: 300, fontSize: 22, margin: '0 0 12px' }}>
                  {current.businessName}
                </h2>
                <Rail items={[
                  ['Contact', `${current.contact.name} · ${current.contact.email}`],
                  // The label comes from the country pack, so this screen never
                  // hardcodes "ABN" — ON-03.
                  [pack?.registrationIdLabel ?? 'Registration', current.registrationId],
                  ...(current.buyingGroup ? [['Buying group', current.buyingGroup] as [string, string]] : []),
                  ['Jurisdiction', `${current.city ?? '—'}, ${current.countryCode}`],
                  ['Tax regime', pack?.taxRegime ?? '—'],
                  ['Waiting', `${current.ageDays} days`],
                ]} />
                {failed ? <Alert tone="stop">{failed}</Alert> : null}

                {/*
                  * ON-02a, ON-02, D94 — the EVIDENCE, above the account setup.
                  *
                  * Above it deliberately. The two things a reviewer does on this screen are
                  * "satisfy myself this is a real business" and "decide what the account gets",
                  * in that order, and the second is pointless if the first fails. It also puts
                  * the reason an approval is about to be refused at the top of the form rather
                  * than in a message after the click.
                  *
                  * Trade references come with it, in the same component, because they are the
                  * other half of the same question and were collected and shown nowhere.
                  */}
                <KycEvidence
                  applicationId={current.id}
                  canEdit={can('retailer.approve')}
                  canDownload={can('retailer.approve')}
                />

                <div style={{ marginTop: 14 }}>
                  <b>Account setup</b>
                  <p className="mk-dim">
                    ON-05 — completed before credentials are issued. Approval is refused if this
                    portal needs a budget and none is given.
                  </p>
                  <div className="mk-fpair">
                    <Field
                      label="Inventory budget"
                      hint={setupDefaults?.defaultBudget
                        ? `The wholesale value this retailer may hold. Leave blank to use this portal's default of ${setupDefaults.defaultBudget} ${setupDefaults.currency}.`
                        : "The wholesale value this retailer may hold. This portal sets no default, so a budget is required here — it is the only limit on ordering."}
                    >
                      <input
                        inputMode="decimal" value={budget} placeholder="e.g. 40000.00"
                        onChange={(e) => setBudget(e.target.value)}
                      />
                    </Field>
                    {/*
                      * BG-04 — "5% difference should be accepted and make it configurable at
                      * retailer level by admin" (client, 29-Oct-2026). Beside the budget,
                      * because a percentage read on its own means nothing.
                      *
                      * The portal's figure is in the placeholder AND in the hint. An empty box
                      * with no figure beside it reads as "this account gets no tolerance",
                      * which is the opposite of what blank actually does.
                      */}
                    <Field
                      label="Tolerance (% over budget accepted)"
                      hint={setupDefaults
                        ? `How far over its available budget an order may still go through. Leave blank to use this portal's ${setupDefaults.budgetTolerancePct}%. Enter 0 to accept nothing over the budget.`
                        : 'How far over its available budget an order may still go through. Leave blank to use the portal\'s figure; enter 0 to accept nothing over the budget.'}
                    >
                      <input
                        inputMode="decimal" value={tolerance}
                        placeholder={setupDefaults?.budgetTolerancePct}
                        onChange={(e) => setTolerance(e.target.value)}
                      />
                    </Field>
                    {/*
                      * D75, RESOLVED (client, 29-Oct-2026): "a retailer's payment term is
                      * configurable per account and overrides the portal's due rule."
                      *
                      * So this box now decides when this account's invoices fall due, and the
                      * hint has to say what BLANK does — which is not "net 30". It is the
                      * portal's rule, whose shape may be month-end, and the only honest way to
                      * state it is to name the rule itself. The hint used to end "...is D75",
                      * which was accurate while the decision was open and is now stale.
                      */}
                    <Field
                      label="Payment terms (days from invoice)"
                      hint={setupDefaults
                        ? `FN-11, D75 — this overrides the portal's due rule for this account. Leave blank to use the portal's rule: ${t(portalRulePhrase.key, portalRulePhrase.values)}. Enter 0 for payable immediately.`
                        : "FN-11, D75 — this overrides the portal's due rule for this account. Leave blank to use the portal's rule; enter 0 for payable immediately."}
                    >
                      <input
                        inputMode="numeric" value={termsDays}
                        placeholder="Portal's rule"
                        onChange={(e) => setTermsDays(e.target.value)}
                      />
                    </Field>
                  </div>
                  <Field label="Why this budget" hint="Recorded against the opening budget. Optional when the portal default is used.">
                    <input
                      value={budgetReason} placeholder="Agreed at onboarding"
                      onChange={(e) => setBudgetReason(e.target.value)}
                    />
                  </Field>

                  {/*
                    * SB-01 — asked for ONLY where the portal charges one.
                    *
                    * This block used to show on every portal, with the server left to refuse a
                    * term it does not take. On GCC that is four fields nobody can fill
                    * correctly: leave them blank and nothing happens, fill them in and the
                    * approval is REFUSED for carrying a subscription the market does not
                    * charge. A form that offers a choice with one legal answer is not being
                    * cautious, it is wasting the reader's attention and inviting a mistake.
                    *
                    * `offers()` reads the acting portal's capability, the same source the
                    * server checks, so the two cannot drift (D46 — never a country check).
                    */}
                  {offersSubscriptions ? (
                  <>
                  <p className="mk-dim" style={{ marginTop: 10 }}>
                    <b>Annual subscription</b> — required on this portal, and the fee is paid
                    before the account is set up. All four together, or none.
                  </p>
                  <div className="mk-fpair">
                  <Field label="Term starts" hint="YYYY-MM-DD.">
                    <input value={subStart} placeholder="2026-11-01" onChange={(e) => setSubStart(e.target.value)} />
                  </Field>
                  <Field label="Renews on" hint="Must be after the start date.">
                    <input value={subEnd} placeholder="2027-10-31" onChange={(e) => setSubEnd(e.target.value)} />
                  </Field>
                  <Field label="Annual fee" hint="In the market's own currency.">
                    <input inputMode="decimal" value={subFee} placeholder="1500.00" onChange={(e) => setSubFee(e.target.value)} />
                  </Field>
                  <Field label="Fee reference" hint="The bank reference or receipt the fee was paid with.">
                    <input value={subRef} placeholder="BNK-00123" onChange={(e) => setSubRef(e.target.value)} />
                  </Field>
                  </div>
                  </>
                  ) : null}
                </div>
                {/*
                  * PAYMENT STAGES ARE GONE FROM THIS SCREEN — retired at v1.6.
                  *
                  * "Retired: ... advance-before-manufacturing, staged payment terms ..."
                  * (BRD §1.6), and D31/D34/F4: "No negotiation, no advance, no payment before
                  * dispatch." Under consignment there are no manufacturing milestones to pay
                  * against: Miraki places the pieces, the retailer reports each sale, and only
                  * sold pieces become payable.
                  *
                  * This was not merely optional before — the editor insisted every ticked
                  * stage carried a percentage above zero and that they summed to 100, so an
                  * approval could be blocked by a schedule the business no longer operates.
                  * It defaulted four stages ticked, which is how the refusal appeared without
                  * anyone choosing anything.
                  *
                  * What survives is `paymentTermsDays` above — FN-11, net days from invoice,
                  * which is a different thing and still real. The editor itself stays in the
                  * codebase for A-04's Trading tab, where existing agreements are still read.
                  */}
                {/* D105 — a WARNING, not a block: KYC gates the order, so Admin may still approve. */}
                {approvalWarning ? <Alert tone="them">{approvalWarning}</Alert> : null}
                <div style={{ marginTop: 14 }}>
                  <Field label="Reason or note" hint="Required to reject or to ask for more information.">
                    <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 7, padding: '14px 15px', borderTop: '1px solid var(--mk-rule)', background: 'var(--mk-surface-2)', flexWrap: 'wrap' }}>
                <Button size="sm" onClick={() => void act('approve')}
                  // Nothing on this screen can block an approval any more. The budget is
                  // the server's to require, per portal, and it says so in its refusal.
                  disabled={decide.isPending}>
                  Approve &amp; create retailer
                </Button>
                <Button size="sm" tone="ghost" disabled={decide.isPending || reason.trim().length < 3}
                  onClick={() => void act('request-information')}>
                  Request information
                </Button>
                <span className="mk-spacer" />
                <Button size="sm" tone="ghost" disabled={decide.isPending || reason.trim().length < 3}
                  onClick={() => void act('reject')}>
                  Reject
                </Button>
              </div>
            </Panel>
          ) : null}
        </div>
      )}

      {decided.length > 0 ? (
        <Panel title={`${decided.length} completed`}>
          {decided.map((a) => (
            <div
              key={a.id}
              className="mk-qrow"
              style={{
                padding: '11px 14px',
                borderBottom: '1px solid var(--mk-rule)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Chip tone={TONE[a.status]}>{a.status.replace('_', ' ')}</Chip>
                <b>{a.businessName}</b>
                <span className="mk-spacer" />
                <span className="mk-hint">{a.ageDays}d</span>
              </div>
              <div className="mk-hint" style={{ marginTop: 3 }}>
                {a.reference} · {a.city ?? '—'}, {a.countryCode} · {a.contact.email}
              </div>
            </div>
          ))}
        </Panel>
      ) : null}
    </>
  );
}
